import {type ReactNode, useCallback, useRef, useState} from 'react'
import {useRelayEnvironment} from 'react-relay'
import {ArrowUpRightIcon, CopilotIcon, IssueOpenedIcon, PlusCircleIcon, TriangleDownIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, ButtonGroup, IconButton, useConfirm} from '@primer/react'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import {CommandActionListItem, GlobalCommands, CommandButton} from '@github-ui/ui-commands'
import type {IssuePickerItem} from '@github-ui/item-picker/IssuePicker'
import {useAnalytics} from '@github-ui/use-analytics'
import {useNavigate} from '@github-ui/use-navigate'
import {useSafeChatState} from '@github-ui/copilot-chat-contexts/ChatStateContext'
import {useShouldShowCopilotFunctionalityInIssues} from '@github-ui/use-should-show-copilot-functionality-in-issues'

import {RelationshipsAlertDialog} from '@github-ui/issue-viewer/RelationshipsAlertDialog'
import {useAddSubIssueMutation} from '../mutations/use-sub-issue-membership-mutations'
import type {SubIssueParentData} from '../mutations/sub-issue-membership-operations'
import type {SubIssueOptimisticNodeInput} from '../mutations/sub-issue-optimistic-node'
import {RepositoryAndIssuePicker} from '@github-ui/item-picker/RepositoryAndIssuePicker'
import type {RelayCompat} from '@github-ui/server-persisted-queries/relay-compat'
import type {IssueViewerSecondaryViewServerQueryQuery} from '@github-ui/server-persisted-queries/types/IssueViewerSecondaryViewServerQuery'
import {useSubIssueFeedback, useSubIssueState} from './SubIssueStateContext'
import {SubIssueFeedbackMessage} from './SubIssueFeedbackMessage'
import {useAlert} from '../utils/use-alert'

import styles from './AddSubIssueButtonGroup.module.css'

const COPILOT_PATH = '/copilot'
const SUB_ISSUES_BREADTH_LIMIT = 100

function parseNameWithOwner(value: string) {
  const [owner, repo, ...extra] = value.split('/')
  if (!owner || !repo || extra.length > 0) throw new Error('Invalid repository nameWithOwner')
  return {owner, repo}
}

type IssueSecondaryNode = NonNullable<NonNullable<IssueViewerSecondaryViewServerQueryQuery['repository']>['issue']>

// parent/subIssues are only fetched when fetchSubIssues is true, so they are optional here.
export type AddSubIssueButtonGroupFragment = RelayCompat<{
  readonly id: IssueSecondaryNode['id']
  readonly databaseId: IssueSecondaryNode['databaseId']
  readonly number: IssueSecondaryNode['number']
  readonly state: IssueSecondaryNode['state']
  readonly stateReason: IssueSecondaryNode['stateReason']
  readonly subIssuesSummary: IssueSecondaryNode['subIssuesSummary']
  readonly title: IssueSecondaryNode['title']
  readonly titleHTML: IssueSecondaryNode['titleHTML']
  readonly url: IssueSecondaryNode['url']
  readonly repository: Pick<
    IssueSecondaryNode['repository'],
    'name' | 'nameWithOwner' | 'visibility' | 'viewerCanPush'
  > & {
    readonly owner: Pick<IssueSecondaryNode['repository']['owner'], 'login'>
  }
  readonly parent?: {readonly id: IssueSecondaryNode['id']} | null
  readonly subIssues?: {
    readonly nodes?: ReadonlyArray<{readonly id: IssueSecondaryNode['id']} | null> | null
  } | null
}>

/**
 * Maps a picked existing issue to the overlay node input. Exported so the picker→overlay field
 * mapping is unit-tested (existing tests only assert the Relay mutation input, not this mapping).
 */
export function toOverlaySubIssueInput(subIssue: IssuePickerItem): SubIssueOptimisticNodeInput {
  return {
    id: subIssue.id,
    title: subIssue.title,
    number: subIssue.number,
    url: subIssue.url,
    state: subIssue.state,
    stateReason: subIssue.stateReason ?? null,
    databaseId: subIssue.databaseId ?? null,
    nameWithOwner: subIssue.repository.nameWithOwner,
  }
}

export function AddSubIssueButtonGroup({
  issue: data,
  insideSidePanel,
  trailingAction,
}: {
  issue: AddSubIssueButtonGroupFragment
  insideSidePanel?: boolean
  trailingAction?: ReactNode
}) {
  const [open, setOpen] = useState(false)
  const {alert, resetAlert, showBreadthLimitAlert, showServerAlert} = useAlert()
  const [pickerType, setPickerType] = useState<'Issue' | 'Repository' | null>(null)
  const {openCreateDialog, createDialogOpen} = useSubIssueState()
  const {feedback, clearFeedback, reportSubIssueAdded} = useSubIssueFeedback()
  const {addToast} = useToastContext()
  const anchorRef = useRef(null)
  const returnFocusRef = useRef<HTMLButtonElement>(null)
  const confirm = useConfirm()
  const {sendAnalyticsEvent} = useAnalytics()
  const navigate = useNavigate()
  const environment = useRelayEnvironment()
  let repositoryCoordinates: ReturnType<typeof parseNameWithOwner> | undefined
  try {
    repositoryCoordinates = parseNameWithOwner(data.repository.nameWithOwner)
  } catch {
    // Coordinates are validated and surfaced when the user attempts the mutation.
  }
  const {addSubIssue: addSubIssueNative} = useAddSubIssueMutation(
    {
      owner: repositoryCoordinates?.owner,
      repo: repositoryCoordinates?.repo,
      number: data.number,
      parent: {
        id: data.id,
        number: data.number,
        state: data.state,
        stateReason: data.stateReason ?? null,
        subIssuesSummary: data.subIssuesSummary,
        title: data.title,
        titleHTML: data.titleHTML,
        url: data.url,
        repository: {
          name: data.repository.name,
          nameWithOwner: data.repository.nameWithOwner,
          owner: {login: data.repository.owner.login},
        },
      } satisfies SubIssueParentData,
      parentIssueId: data.id,
    },
    environment,
  )

  const subIssueCount = data.subIssues?.nodes?.length || 0
  const subIssuesBreadthLimitReached = subIssueCount >= SUB_ISSUES_BREADTH_LIMIT

  const chatState = useSafeChatState()
  const showSubIssuesCopilotMenuItem = useShouldShowCopilotFunctionalityInIssues({
    repositoryVisibility: data.repository.visibility,
    userCanPushToRepo: data.repository.viewerCanPush,
    chatMode: chatState?.mode,
  })

  type AddSubIssueParams = {
    subIssue: IssuePickerItem & {hasParent?: boolean}
    onError?: (error: Error) => void
  }

  const addSubIssue = useCallback(
    async ({subIssue, onError}: AddSubIssueParams) => {
      const shouldAddSubIssue =
        !subIssue.hasParent ||
        (await confirm({
          title: 'Are you sure?',
          content: 'Sub-issues are limited to one parent. Confirm you want to proceed with this change.',
          confirmButtonType: 'primary',
          confirmButtonContent: 'Change parent issue',
        }))

      if (!shouldAddSubIssue) return
      try {
        parseNameWithOwner(data.repository.nameWithOwner)
        await addSubIssueNative(toOverlaySubIssueInput(subIssue))
      } catch (error) {
        onError?.(error instanceof Error ? error : new Error('Failed to add sub-issue'))
        return
      }
      reportSubIssueAdded(data.id)
    },
    [addSubIssueNative, confirm, data.id, data.repository.nameWithOwner, reportSubIssueAdded],
  )

  const onIssueSelection = useCallback(
    (issues: IssuePickerItem[]) => {
      // At this time, only single issue selection is supported
      const [subIssue] = issues
      if (!subIssue) return

      // Parent data is now included via withParent prop on the picker
      const selectedSubIssue = {
        hasParent: Boolean(subIssue.parent?.id),
        ...subIssue,
      }

      addSubIssue({
        subIssue: selectedSubIssue,
        onError: error => {
          if (!showServerAlert(error)) {
            // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
            addToast({
              type: 'error',
              message: error.message,
            })
          }
        },
      })
    },
    [addSubIssue, addToast, showServerAlert],
  )

  const onPickerTypeChange = useCallback(
    (type: 'Issue' | 'Repository' | null) => {
      // When the picker is closed, focus the trigger
      if (type === null) {
        returnFocusRef.current?.focus()
      }

      setPickerType(type)
    },
    [setPickerType],
  )

  const onAlertClose = useCallback(() => resetAlert(), [resetAlert])

  const openSubIssueCreateDialog = useCallback(() => {
    if (subIssuesBreadthLimitReached) {
      showBreadthLimitAlert(SUB_ISSUES_BREADTH_LIMIT)
    } else {
      // Ensure the repo/issue picker and action menu is closed when the dialog is opened
      setOpen(false)
      setPickerType(null)
      openCreateDialog(data.id)
    }
  }, [data.id, openCreateDialog, showBreadthLimitAlert, subIssuesBreadthLimitReached])

  const navigateToSubIssuesCopilot = useCallback(() => {
    sendAnalyticsEvent('analytics.click', 'ISSUES_SUB_ISSUES_COPILOT_BUTTON', {
      issueId: data.databaseId,
      issueNumber: data.number,
      repoNWO: data.repository.nameWithOwner,
    })

    const prompt = `Create me a sub-issue for @${data.repository.nameWithOwner}/issues/${data.number} to `
    const params = new URLSearchParams({prompt})
    navigate(`${COPILOT_PATH}?${params.toString()}`)
  }, [sendAnalyticsEvent, data.databaseId, data.repository.nameWithOwner, data.number, navigate])

  /**
   * When the issue-viewer is rendered within the side-panel, two issue-viewers are rendered at a time. This results
   * in `<GlobalCommands>` activating both pickers at once. To remedy this, we check if the side-panel is open and
   * whether or not the current issue-viewer is within the side-panel.
   *
   * Commands are only accepted when both the picker and create dialog are closed.
   *
   * If insideSidePanel is false, the issue-viewer is not inside the open side-panel.
   * If insideSidePanel is undefined, the side panel is not open.
   */
  const shouldAcceptCommand =
    (insideSidePanel || insideSidePanel === undefined) && pickerType === null && !createDialogOpen

  const hiddenIssueIds = data.subIssues?.nodes?.filter(node => !!node).map(i => i.id) ?? []

  // Ensure the current issue is not selectable
  hiddenIssueIds.push(data.id)

  // Disallow selecting parent as sub-issue
  if (data.parent) {
    hiddenIssueIds.push(data.parent.id)
  }

  const handleMenuOpenChange = (newOpen: boolean) => {
    setOpen(newOpen)
    // Ensure the repo/issue picker is closed when the menu opens
    if (newOpen) {
      setPickerType(null)
      clearFeedback()
    }
  }

  const currentFeedback = feedback?.parentIssueId === data.id ? feedback : null

  const subIssueOptionsOverlay = (
    <ActionMenu.Overlay width="small" returnFocusRef={returnFocusRef}>
      <ActionList>
        <ActionList.Group>
          <CommandActionListItem commandId="sub-issues:create-sub-issue" leadingVisual={<PlusCircleIcon />}>
            Create sub-issue
          </CommandActionListItem>
          <CommandActionListItem commandId="sub-issues:add-existing-issue" leadingVisual={<IssueOpenedIcon />}>
            Add existing issue
          </CommandActionListItem>
          {showSubIssuesCopilotMenuItem && (
            <>
              <ActionList.Divider />
              <ActionList.Item onSelect={navigateToSubIssuesCopilot}>
                <ActionList.LeadingVisual>
                  <CopilotIcon />
                </ActionList.LeadingVisual>
                Draft with Copilot
                <ActionList.TrailingVisual>
                  <ArrowUpRightIcon />
                </ActionList.TrailingVisual>
              </ActionList.Item>
            </>
          )}
        </ActionList.Group>
      </ActionList>
    </ActionMenu.Overlay>
  )

  return (
    <>
      {alert && (
        <RelationshipsAlertDialog title={alert.title} onClose={onAlertClose}>
          {alert.body}
        </RelationshipsAlertDialog>
      )}
      <div className={styles.Box}>
        {shouldAcceptCommand && (
          <GlobalCommands
            commands={{
              'sub-issues:add-existing-issue': () => {
                if (subIssuesBreadthLimitReached) {
                  showBreadthLimitAlert(SUB_ISSUES_BREADTH_LIMIT)
                } else {
                  setPickerType('Issue')
                }
              },
              'sub-issues:create-sub-issue': openSubIssueCreateDialog,
            }}
          />
        )}
        <ButtonGroup ref={anchorRef}>
          <CommandButton
            size="small"
            commandId="sub-issues:create-sub-issue"
            onClick={event => {
              event.preventDefault()
              openSubIssueCreateDialog()
            }}
          >
            Create sub-issue
          </CommandButton>
          {/* Render the caret through ActionMenu.Anchor so Primer wires the menu-button
              semantics (generated trigger id, aria-haspopup, aria-expanded, keyboard handler)
              and labels the overlay by this trigger. anchorRef={returnFocusRef} both anchors
              the menu to the caret and keeps a handle for returning focus after the picker
              closes; the surrounding ButtonGroup still anchors the RepositoryAndIssuePicker. */}
          <ActionMenu open={open} onOpenChange={handleMenuOpenChange} anchorRef={returnFocusRef}>
            <ActionMenu.Anchor>
              {/* eslint-disable-next-line primer-react/a11y-remove-disable-tooltip */}
              <IconButton
                unsafeDisableTooltip
                size="small"
                icon={TriangleDownIcon}
                aria-label="View more sub-issue options"
              />
            </ActionMenu.Anchor>
            {subIssueOptionsOverlay}
          </ActionMenu>
        </ButtonGroup>
        {trailingAction}
        {currentFeedback && <SubIssueFeedbackMessage key={currentFeedback.id} feedback={currentFeedback} />}
        <RepositoryAndIssuePicker
          selectedIssueIds={[]}
          hiddenIssueIds={hiddenIssueIds}
          onPickerTypeChange={onPickerTypeChange}
          onIssueSelection={onIssueSelection}
          defaultRepositoryNameWithOwner={data.repository.nameWithOwner}
          organization={data.repository.owner.login}
          pickerType={pickerType}
          withParent
          anchorElement={props => {
            const {ref} = props as React.HTMLAttributes<HTMLButtonElement> & {
              ref: React.RefObject<HTMLButtonElement | null>
            }
            if (ref) {
              ref.current = anchorRef.current
            }
            // We don't actually want to render any element, as we are relying on the rendered section header above
            return <></>
          }}
        />
      </div>
    </>
  )
}
